import difference from 'lodash/difference';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import Context from '@bibliocommons/context';
import { getCookie, setCookie } from '@bibliocommons/utils-browser';

import { HIDDEN_NOTICES_COOKIE } from 'app/constants/NoticeConstants';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectBranding } from 'app/selectors/BrandingSelector';
import SystemMessages from './SystemMessages';

const getDismissedMessageIds = () => {
  const serializedDismissedMessageIds = getCookie(HIDDEN_NOTICES_COOKIE) || '';
  const dismissedMessageIds = serializedDismissedMessageIds.length ? serializedDismissedMessageIds.split(':') : [];

  return dismissedMessageIds.map(Number);
};

const setDismissedMessageIds = dismissedMessageIds => {
  setCookie(HIDDEN_NOTICES_COOKIE, dismissedMessageIds.join(':'), { path: '/' });
};

const SystemMessagesContainer = ({ destination = 'CORE', priority = 'CRITICAL' }) => {
  const priorityQueryParam = priority !== 'ALL' ? `&priority=${priority}` : '';

  const { mobileApp: isMobileApp } = useContext(Context);
  const intl = useIntl();
  const branding = useSelector(selectBranding);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const cmsEnabled = currentLibrary && currentLibrary.get('cmsEnabled');
  const apiGatewayUrl = useSelector(state => state.getIn(['app', 'apiGatewayURL']));
  const libraryDomain = useSelector(state => state.getIn(['app', 'libraryDomain']));
  const isLocalBranch = useSelector(state => Boolean(state.getIn(['app', 'localBranch'])));

  const [state, setState] = useState({
    dismissedMessageIds: [],
    messageIndex: {},
    orderedMessageIds: []
  });

  const systemMessageBackgroundColor = cmsEnabled ? branding.get('systemMessageBackgroundColor') : null;
  const systemMessageTextColor = cmsEnabled ? branding.get('systemMessageTextColor') : null;

  useEffect(() => {
    fetch(`${apiGatewayUrl}/libraries/${libraryDomain}/systemMessages?destination=${destination}${priorityQueryParam}`)
      .then(res => {
        if (!res.ok) {
          return { entities: { items: [] }, systemMessages: {} };
        }

        return res.json();
      })
      .then(({ entities, systemMessages }) => {
        const { systemMessages: messageIndex } = entities;
        const { items: orderedMessageIds } = systemMessages;

        setState(prevState => ({
          ...prevState,
          messageIndex,
          orderedMessageIds
        }));
      })
      .catch(() => {});
  }, [apiGatewayUrl, destination, libraryDomain, priorityQueryParam]);

  useEffect(() => {
    if (isLocalBranch) {
      return;
    }

    const dismissedMessageIds = getDismissedMessageIds();

    setState(prevState => ({
      ...prevState,
      dismissedMessageIds
    }));
  }, [isLocalBranch]);

  const dismissMessage = messageId => {
    const dismissedMessageIds = [...state.dismissedMessageIds, messageId];

    setState(prevState => ({
      ...prevState,
      dismissedMessageIds
    }));

    setDismissedMessageIds(dismissedMessageIds);
  };

  const messages = useMemo(() => {
    const { dismissedMessageIds, messageIndex, orderedMessageIds } = state;

    return difference(orderedMessageIds, dismissedMessageIds).map(messageId => messageIndex[messageId]);
  }, [state]);
  if (isMobileApp) {
    return null;
  }

  const hasMessages = messages.length > 0;

  return (
    <>
      <div aria-live="polite">
        <ScreenReaderMessage>
          {intl.formatMessage({ id: 'label_system_messages' }, { numTotalMessages: messages.length })}
        </ScreenReaderMessage>
      </div>
      {hasMessages && (
        <SystemMessages
          dismissMessage={dismissMessage}
          intl={intl}
          isDismissible={!isLocalBranch}
          messages={messages}
          systemMessageBackgroundColor={systemMessageBackgroundColor}
          systemMessageTextColor={systemMessageTextColor}
        />
      )}
    </>
  );
};

SystemMessagesContainer.defaultProps = {
  destination: 'CORE',
  priority: 'CRITICAL'
};

SystemMessagesContainer.propTypes = {
  destination: PropTypes.oneOf(['EVERYWHERE', 'CORE', 'CMS']),
  priority: PropTypes.oneOf(['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'])
};

export default SystemMessagesContainer;
